import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAssistantById,
  recrawlAssistant,
} from "../../../actions/assistants";
import {
  GlobeIcon,
  CodeIcon,
  EnvelopeIcon,
  DiamondIcon,
  ChevronRightIcon,
  // PencilIcon,
  InfoIcon,
  CheckIcon,
  RefreshIcon,
  RocketIcon,
  KebabIcon,
  SendIcon,
  LockIcon,
  SlidersIcon,
} from "./icons";
import { buildEmbedLines, buildEmbedSnippet } from "../../../utils/embed";
import { readWidgetTheme } from "../../../utils/widgetTheme";
import WidgetGlyph from "../../../Components/WidgetGlyph";

const installOptions = [
  {
    id: "html",
    icon: <CodeIcon />,
    title: "HTML Website",
    detail: "Add the script before </body>",
  },
  {
    id: "wordpress",
    icon: "W",
    variant: "wp",
    title: "WordPress",
    detail: "Use a header/footer or code plugin",
  },
  {
    id: "gtm",
    icon: <DiamondIcon />,
    variant: "gtm",
    title: "Google Tag Manager",
    detail: "Install through your GTM container",
  },
  {
    id: "developer",
    icon: <EnvelopeIcon />,
    title: "Send to developer",
    detail: "Email installation instructions",
    noChevron: true,
  },
];

// status comes back as crawling | ready | failed.
const STATUS_LABELS = {
  crawling: "Reading your website",
  processing: "Reading your website",
  pending: "Queued",
  queued: "Queued",
  ready: "Ready to go live",
  failed: "Crawl failed",
};

// Anything still working keeps the poll alive; ready or failed ends it.
const PENDING_STATUSES = ["crawling", "pending", "queued", "processing"];

// The crawl can still be running when this step opens, so the status is
// re-read on a timer rather than only when the user clicks "check again".
const POLL_INTERVAL_MS = 5000;

const features = [
  {
    icon: <LockIcon />,
    title: "Secure & private",
    detail: "We only access what you allow.",
  },
  {
    icon: <CodeIcon />,
    title: "No coding required",
    detail: "Get started in under 2 minutes.",
  },
  {
    icon: <SlidersIcon />,
    title: "You can customize everything later",
    detail: "Fine-tune your assistant any time.",
  },
];

export default function StepInstall({ assistantId = "", onGoLive }) {
  const dispatch = useDispatch();
  const assistant = useSelector((state) => state.assistants.current);
  const id = assistantId || assistant?.id || "";
  // The colour and avatar a visitor will actually see, rather than the app's
  // own indigo and a robot.
  const theme = readWidgetTheme(assistant);
  const brandStyle = theme.primaryColor
    ? { background: theme.primaryColor }
    : undefined;

  const [selectedOption, setSelectedOption] = useState("html");
  const [copied, setCopied] = useState(false);
  // `copied` resets after 1.5s for the button label; this stays set so the
  // checklist keeps its tick.
  const [hasCopied, setHasCopied] = useState(false);
  const [editingDomain, setEditingDomain] = useState(false);
  const [domain, setDomain] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");

  // Step 2 normally leaves the record in the store; this covers a direct load.
  useEffect(() => {
    if (id && !assistant) dispatch(getAssistantById(id));
  }, [dispatch, id, assistant]);

  // Only seed the field once, so an in-progress edit isn't clobbered.
  useEffect(() => {
    if (assistant?.allowedDomain)
      setDomain((prev) => prev || assistant.allowedDomain);
  }, [assistant]);

  // The crawl often hasn't finished when this step opens. Without this the
  // status sat on "Reading your website" until the user clicked check-again;
  // now it flips to "Ready to go live" on its own.
  const stillCrawling = PENDING_STATUSES.includes(assistant?.status || "");

  useEffect(() => {
    if (!id || !stillCrawling) return undefined;

    let cancelled = false;
    let timer = null;

    const stop = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };

    // Chained rather than setInterval so a slow response can't stack calls.
    const tick = async () => {
      await dispatch(getAssistantById(id));
      if (cancelled) return;
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return stop;
  }, [dispatch, id, stillCrawling]);

  const name = assistant?.name || "your assistant";
  const status = assistant?.status || "";
  // The snippet carries the public id, not the internal record id.
  const publicId = assistant?.publicId || "";
  const embedLines = buildEmbedLines(publicId);

  const crawling = PENDING_STATUSES.includes(status);
  const ready = status === "ready";
  const failed = Boolean(status) && !crawling && !ready;

  const verificationRows = [
    { label: "Website", value: assistant?.sourceUrl || "—" },
    { label: "Assistant", value: assistant?.name || "—" },
    { label: "Domain", value: assistant?.allowedDomain || "—" },
    { label: "Assistant ID", value: id || "—" },
    {
      label: "Status",
      value: STATUS_LABELS[status] || status || "—",
      highlight: ready,
      failed,
      // Drives the inline spinner while the crawl is still running.
      busy: crawling,
    },
  ];

  // Each row only earns its tick once it actually has a value.
  const rowsWithState = verificationRows.map((row) => ({
    ...row,
    done: row.label === "Status" ? ready : row.value !== "—",
  }));

  const checklist = [
    { label: "Website crawled", done: ready },
    { label: "Embed code copied", done: hasCopied },
    { label: "Domain verified", done: Boolean(assistant?.allowedDomain) },
    {
      label: "Ready to publish",
      done: ready && Boolean(assistant?.allowedDomain),
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildEmbedSnippet(publicId));
      setCopied(true);
      setHasCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard not available; ignore
    }
  };

  // Re-reads the site and then the record, so a crawl that failed or ran
  // before the snippet went up can be retried from here rather than only
  // refetching a status that will never change on its own.
  const handleCheckAgain = async () => {
    if (!id) return;

    setChecking(true);

    const started = await dispatch(recrawlAssistant(id));

    // A refused recrawl still leaves the record worth re-reading: the crawl
    // may simply have finished already.
    if (!started?.success) setCheckError(started.message || "");
    else setCheckError("");

    await dispatch(getAssistantById(id));

    setChecking(false);
  };

  return (
    <>
      <h1 className="onboarding__title">Install and go live</h1>
      <p className="onboarding__subtitle">
        Add Hostor to your website and launch your AI assistant in just a few
        steps.
      </p>

      <div className="onboarding__grid onboarding__grid--install">
        <section className="onboarding__card">
          <div className="install-embed-header">
            <h2 className="onboarding__card-title">Your embed code</h2>
            <button
              type="button"
              className="install-copy-btn"
              onClick={handleCopy}
              disabled={!publicId}
            >
              <CopyIconOrCheck copied={copied} />
              {copied ? "Copied!" : "Copy code"}
            </button>
          </div>

          <pre className="install-code">
            <code>
              {embedLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line.indent && "  "}
                  {line.attr && <span className="tok-attr">{line.attr}</span>}
                  {line.attr && line.value && "="}
                  {line.value && (
                    <span className="tok-str">&quot;{line.value}&quot;</span>
                  )}
                  {line.tag && <span className="tok-tag">{line.tag}</span>}
                  {i < embedLines.length - 1 && "\n"}
                </React.Fragment>
              ))}
            </code>
          </pre>

          <h3 className="install-section-label">Installation options</h3>
          <ul className="install-option-list">
            {installOptions.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className={`install-option${selectedOption === option.id ? " is-selected" : ""}`}
                  onClick={() => setSelectedOption(option.id)}
                  aria-pressed={selectedOption === option.id}
                >
                  <span
                    className={`install-option-icon${option.variant ? ` install-option-icon--${option.variant}` : ""}`}
                  >
                    {option.icon}
                  </span>
                  <span className="install-option-text">
                    <strong>{option.title}</strong>
                    <span>{option.detail}</span>
                  </span>
                  {!option.noChevron && <ChevronRightIcon />}
                </button>
              </li>
            ))}
          </ul>

          <span className="review-field-label install-domain-label">
            Allowed domain
          </span>
          <div className="install-domain-row">
            <label className="onboarding__input-wrap install-domain-input">
              <GlobeIcon />
              <input
                type="text"
                value={domain}
                readOnly={!editingDomain}
                onChange={(e) => setDomain(e.target.value)}
              />
            </label>
            {/* <button
              type="button"
              className="onboarding__btn onboarding__btn--secondary install-domain-edit"
              onClick={() => setEditingDomain((editing) => !editing)}
            >
              <PencilIcon />
              {editingDomain ? "Save" : "Edit"}
            </button> */}
          </div>

          <p className="install-note">
            <InfoIcon />
            Installation takes only a minute. You can change any setting later.
          </p>
        </section>

        <div className="install-verify-col">
          <section className="onboarding__card">
            <h2 className="onboarding__card-title">Website verification</h2>

            <div className="install-verify-top">
              <ul className="install-verify-rows" aria-live="polite">
                {rowsWithState.map((row) => (
                  <li
                    key={row.label}
                    className={row.busy ? "is-busy" : undefined}
                  >
                    <span className="install-verify-label">{row.label}</span>
                    <span
                      className={`install-verify-value${row.highlight ? " is-highlight" : ""}${row.failed ? " is-failed" : ""}`}
                    >
                      {row.value}
                    </span>
                    <span className="install-verify-check">
                      {row.busy ? (
                        <span
                          className="install-status-spinner"
                          aria-hidden="true"
                        />
                      ) : row.done ? (
                        <span className="onboarding__check-icon">
                          <CheckIcon />
                        </span>
                      ) : (
                        <span
                          className="install-verify-pending"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="install-mini-preview" aria-hidden="true">
                <div className="install-mini-browser">
                  <div className="install-mini-browser-bar">
                    <span />
                    <span />
                    <span />
                    <span className="install-mini-browser-address" />
                  </div>
                  <div className="install-mini-browser-body">
                    <div className="install-mini-browser-media" />
                    <div className="install-mini-browser-lines">
                      <span />
                      <span />
                    </div>
                  </div>
                </div>

                <div className="install-mini-chat">
                  <div className="install-mini-chat-header" style={brandStyle}>
                    <span className="install-mini-chat-avatar">
                      <WidgetGlyph theme={theme} size={13} />
                    </span>
                    <div className="install-mini-chat-heading">
                      <strong>{name}</strong>
                      <span>
                        <span className="review-chat-status-dot" />
                        Online
                      </span>
                    </div>
                    <span className="install-mini-chat-kebab">
                      <KebabIcon />
                    </span>
                  </div>
                  <p className="install-mini-chat-bubble">
                    {assistant?.welcomeMessage || "Hi! How can I help?"}
                  </p>
                  <div className="install-mini-chat-input">
                    <span>Type your message...</span>
                    <span className="install-mini-chat-send">
                      <SendIcon />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <ul className="install-checklist">
              {checklist.map((item) => (
                <li
                  key={item.label}
                  className={item.done ? "is-done" : "is-pending"}
                >
                  {item.done ? (
                    <span className="onboarding__check-icon">
                      <CheckIcon />
                    </span>
                  ) : (
                    <span
                      className="install-verify-pending"
                      aria-hidden="true"
                    />
                  )}
                  {item.label}
                </li>
              ))}
            </ul>
          </section>

          {checkError && <p className="auth-error">{checkError}</p>}

          <div className="install-verify-actions">
            <button
              type="button"
              className="onboarding__btn onboarding__btn--secondary"
              onClick={handleCheckAgain}
              disabled={checking}
            >
              <RefreshIcon className={checking ? "is-spinning" : ""} />
              {checking ? "Rechecking..." : "Check installation again"}
            </button>
            <button
              type="button"
              className="onboarding__btn onboarding__btn--primary"
              onClick={onGoLive}
            >
              Go live
              <RocketIcon />
            </button>
          </div>
        </div>
      </div>

      <div className="onboarding__features">
        {features.map((feature) => (
          <div className="onboarding__feature" key={feature.title}>
            <span className="onboarding__feature-icon">{feature.icon}</span>
            <div>
              <strong>{feature.title}</strong>
              <span>{feature.detail}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function CopyIconOrCheck({ copied }) {
  if (copied) {
    return (
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
      >
        <path
          d="M5 12.5l4.5 4.5L19 7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path
        d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"
        strokeLinecap="round"
      />
    </svg>
  );
}
